import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Loader2, School, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Magic-link redemption for school_admin AND pfp_therapist invites.
 *
 * Reads ?token=…&kind=… from URL. On successful password set, receives
 * a JWT + user from backend, drops them in localStorage, then routes:
 *   - school_admin → /school-admin
 *   - pfp_therapist → /pfp-therapist-home
 */
export default function PfpAcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = params.get('token') || '';
  const kind = params.get('kind') || 'school_admin_invite';

  useEffect(() => {
    if (!token) {
      toast.error('Invalid link');
    }
  }, [token]);

  const isSchoolAdmin = kind === 'school_admin_invite';
  const roleLabel = isSchoolAdmin ? 'school administrator' : 'therapist';

  const submit = async () => {
    if (pw.length < 8) return toast.error('Password must be at least 8 characters');
    if (pw !== pw2) return toast.error('Passwords do not match');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/pfp-invites/redeem`, { token, password: pw });
      const { token: jwt, user, kind: k } = r.data;
      localStorage.setItem('token', jwt);
      localStorage.setItem('user', JSON.stringify(user));
      toast.success('Welcome! Your account is ready.');
      navigate(k === 'school_admin_invite' ? '/school-admin' : '/pfp-therapist-home');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not activate account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-8 rounded-3xl border-primary/10 bg-white">
        <div className="flex flex-col items-center text-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            {isSchoolAdmin ? (
              <School className="w-7 h-7 text-primary" />
            ) : (
              <Sparkles className="w-7 h-7 text-primary" />
            )}
          </div>
          <div>
            <h1 className="text-2xl font-serif text-primary-900 mb-1">
              Welcome to PlayFirst
            </h1>
            <p className="text-sm text-stone-600">
              You've been invited as a <strong>{roleLabel}</strong>. Set a password
              to activate your account.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label>New password</Label>
            <Input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="h-12 rounded-xl mt-1"
              placeholder="At least 8 characters"
              data-testid="invite-password"
              autoFocus
            />
          </div>
          <div>
            <Label>Confirm password</Label>
            <Input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="h-12 rounded-xl mt-1"
              placeholder="Repeat"
              data-testid="invite-password-confirm"
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
          </div>
          <Button
            className="w-full rounded-full h-12"
            onClick={submit}
            disabled={submitting || !token}
            data-testid="activate-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-4 h-4 mr-2" />}
            Activate my account
          </Button>
        </div>
      </Card>
    </div>
  );
}
